import { Input, Modal, message } from '@duxton-web/ui';
import _ from 'lodash';
import {
  cpApprove, cpListCvps, cpRecall, cpReject, cpUpdateCvp,
} from 'omega-rtc/actions/ucd';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  STATUS_PENDING, cvpFromApi, formatExpiry,
} from './adapters';
import { CvpDetail, CvpList } from './CvpsView';
import {
  EMPTY_CONTENT_CONFIG_PERMISSIONS,
  canRecallPendingChange,
  canReviewPendingChange,
  canSubmitCvpUpdate,
} from './permissions';
import useContentConfigWrite from './useContentConfigWrite';

const FORBIDDEN_MESSAGE = "You don't have permission to perform this action.";
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const CvpsPage = ({
  contentConfigPermissions = EMPTY_CONTENT_CONFIG_PERMISSIONS,
  navTick,
}) => {
  const { canUpdate, canReview } = contentConfigPermissions;
  const dispatch = useDispatch();
  const userEmail = useSelector((s) => _.get(s, 'app.user.email', ''));
  const activeReq = useSelector((s) => _.get(s, 'requests.cpListCvpsRequest', EMPTY_OBJECT));
  const pendingReq = useSelector((s) => _.get(s, 'requests.cpListCvpsPendingRequest', EMPTY_OBJECT));
  // Active and pending lists are fetched separately; the list is loading until
  // both have settled.
  const listPending = Boolean(activeReq.isPending) || Boolean(pendingReq.isPending);
  const activeRaw = useSelector((s) => _.get(s, 'ucd.cpCvpsByCountry.__all__.active', EMPTY_ARRAY));
  const pendingRaw = useSelector((s) => _.get(s, 'ucd.cpCvpsByCountry.__all__.pending', EMPTY_ARRAY));

  const fetchCvps = useCallback(() => {
    dispatch(cpListCvps(''));
    dispatch(cpListCvps('', STATUS_PENDING));
  }, [dispatch]);

  useEffect(() => {
    fetchCvps();
  }, [fetchCvps]);

  const activeCvps = useMemo(() => activeRaw.map(cvpFromApi), [activeRaw]);
  const pendingCvps = useMemo(() => pendingRaw.map(cvpFromApi), [pendingRaw]);
  const loading = listPending && activeCvps.length === 0 && pendingCvps.length === 0;

  const merged = useMemo(() => {
    const records = {};
    activeCvps.forEach((cvp) => {
      records[cvp.key] = { ...(records[cvp.key] || {}), key: cvp.key, active: cvp };
    });
    pendingCvps.forEach((cvp) => {
      records[cvp.key] = { ...(records[cvp.key] || {}), key: cvp.key, pending: cvp };
    });
    return records;
  }, [activeCvps, pendingCvps]);

  const [selectedKey, setSelectedKey] = useState('');
  const [descEdit, setDescEdit] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [showPending, setShowPending] = useState(false);

  useEffect(() => {
    setSelectedKey('');
    setDescEdit(null);
    setShowDiff(false);
    setShowPending(false);
  }, [navTick]);

  useEffect(() => {
    if (selectedKey && !merged[selectedKey] && !listPending) {
      setSelectedKey('');
    }
  }, [merged, selectedKey, listPending]);

  useEffect(() => {
    setDescEdit(null);
    setShowDiff(false);
    setShowPending(false);
  }, [selectedKey]);

  const sel = merged[selectedKey];
  const hasPending = Boolean(sel && sel.pending);
  const base = sel && (showPending && sel.pending ? sel.pending : (sel.active || sel.pending));
  const originalDesc = (sel && sel.active && sel.active.desc) || '';
  const description = descEdit === null ? ((base && base.desc) || '') : descEdit;
  const hasEdits = descEdit !== null && descEdit !== originalDesc;
  const pendingAuthor = hasPending ? (sel.pending.updatedBy || sel.pending.createdBy) : '';
  const pendingExpiry = hasPending ? formatExpiry(sel.pending.expiresAt) : '';
  const isMine = pendingAuthor === userEmail;

  useEffect(() => {
    if (hasPending) setDescEdit(null);
  }, [hasPending, selectedKey]);

  const rows = useMemo(() => Object.keys(merged).map((key) => {
    const record = merged[key];
    const row = record.active || record.pending;
    return {
      key,
      desc: row.desc,
      status: record.pending ? 'pending' : 'active',
      updatedBy: row.updatedBy || row.createdBy || '',
      updatedAt: row.updatedAt,
    };
  }), [merged]);

  const onWriteSuccess = useCallback(() => {
    setDescEdit(null);
    setShowDiff(false);
    setShowPending(false);
    fetchCvps();
  }, [fetchCvps]);

  const { busy, runWrite } = useContentConfigWrite({ onSuccess: onWriteSuccess });
  const notifyForbidden = () => message.error(FORBIDDEN_MESSAGE);

  const onSendForApproval = () => {
    const cvpUpdateState = {
      hasActive: Boolean(sel && sel.active),
      hasDescription: Boolean(description.trim()),
      hasEdits,
      hasPending,
    };

    if (!sel || !sel.active) return;
    if (!canUpdate) {
      notifyForbidden();
      return;
    }
    if (!canSubmitCvpUpdate(contentConfigPermissions, cvpUpdateState)) return;
    runWrite(cpUpdateCvp(sel.active.id, description, userEmail), 'updateCvp');
  };
  const onApprove = () => {
    if (!sel || !sel.pending) return;
    if (!canReviewPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    runWrite(cpApprove(sel.pending.id, userEmail), 'approve');
  };
  const onRecall = () => {
    if (!sel || !sel.pending) return;
    if (!canRecallPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    runWrite(cpRecall(sel.pending.id, userEmail), 'recall');
  };
  const onReject = () => {
    if (!sel || !sel.pending) return;
    if (!canReviewPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    const pendingId = sel.pending.id;
    let comment = '';
    Modal.confirm({
      title: 'Reject pending change?',
      content: (<Input.TextArea placeholder="Reason (required)" onChange={(event) => { comment = event.target.value; }} />),
      okText: 'Reject',
      okType: 'danger',
      onOk: () => {
        if (!canReviewPendingChange(contentConfigPermissions, isMine)) { notifyForbidden(); return Promise.reject(); }
        if (!comment.trim()) { message.error('A rejection comment is required.'); return Promise.reject(); }
        runWrite(cpReject(pendingId, userEmail, comment), 'reject');
        return Promise.resolve();
      },
    });
  };

  return (
    <div className="cc-offerings-page">
      {selectedKey ? (
        <CvpDetail
          base={base}
          busy={busy}
          canReview={canReview}
          canUpdate={canUpdate}
          description={description}
          discardLocalEdits={() => setDescEdit(null)}
          hasEdits={hasEdits}
          hasPending={hasPending}
          isMine={isMine}
          onApprove={onApprove}
          onDescriptionChange={setDescEdit}
          onRecall={onRecall}
          onReject={onReject}
          onSendForApproval={onSendForApproval}
          pendingAuthor={pendingAuthor}
          pendingExpiry={pendingExpiry}
          sel={sel}
          setShowDiff={setShowDiff}
          setShowPending={setShowPending}
          showDiff={showDiff}
          showPending={showPending}
        />
      ) : (
        <CvpList
          loading={loading}
          onOpenDetail={setSelectedKey}
          rows={rows}
        />
      )}
    </div>
  );
};

export default CvpsPage;
